import {ChangeEvent, KeyboardEvent, useEffect, useRef, useState} from 'react';
import {SoopInputBox, SoopToastCommon} from '@soop-ui/soop-ui';
import DOMPurify from 'dompurify';
import {
  useGetChatbotAnswerList,
  useAutoCompleteNavigation,
  useDropdown,
  useAutoComplete,
} from '@/hooks/help';
import {highlightKeyword} from '@/utils/help';
import {sendHelpClickLog} from '@/utils/help/log';
import {HELP_LOG} from '@/constants/help';
import styles from './ChatbotInputArea.module.scss';

interface ChatbotInputAreaProps {
  handleSend: (text: string, callback?: () => void) => void;
}

const ChatbotInputArea = ({handleSend}: ChatbotInputAreaProps) => {
  // 개인정보 입력 주의 토스트 노출 로직
  const [isShownInfo, setIsShownInfo] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsShownInfo(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const {inputValue, debouncedValue, onChange, onClear} = useAutoComplete();

  const {data: autoCompleteList = []} = useGetChatbotAnswerList({
    searchWord: debouncedValue.trim(),
  });

  const {isDropdownOpen, openDropdown, closeDropdown, dropdownAreaRef} =
    useDropdown();

  const sendMessage = (text: string, callback?: () => void) => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      return;
    }
    handleSend(text, () => {
      onClear();
      callback?.();
    });
    closeDropdown();
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleAutoCompleteClick = (title: string) => {
    sendMessage(title, () => {
      sendHelpClickLog(HELP_LOG.CODE_TYPE.CHATBOT, {
        buttonType: HELP_LOG.BUTTON_TYPE.CHATBOT_CONTENTS,
        actionType: HELP_LOG.ACTION_TYPE.TO_AUTO,
        text: title,
      });
    });
    // 자동완성 선택 시 인풋 포커스
    inputRef.current?.focus();
  };

  const {listWrapperProps, listRefs, selectedIndex, onInputKeyDown} =
    useAutoCompleteNavigation<HTMLUListElement>({
      list: autoCompleteList,
      onEnterKeyUp: (text: unknown) => handleAutoCompleteClick(text as string),
      onESCKeyUp: closeDropdown,
    });

  const onInputKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendMessage(inputValue);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    openDropdown();
  };

  return (
    <div ref={dropdownAreaRef}>
      {isShownInfo && (
        <div className={styles.ToastArea}>
          <SoopToastCommon message='주민등록번호, 전화번호, 계좌번호 등 개인정보를 입력하지 말아주세요.' />
        </div>
      )}

      {isDropdownOpen && inputValue && autoCompleteList.length > 0 && (
        <div className={styles.RelatedSearchArea}>
          <ul {...listWrapperProps}>
            {autoCompleteList.map((text: string, index: number) => (
              <li
                key={text}
                className={
                  selectedIndex === index ? styles.selectedItem : undefined
                }
                ref={(el: HTMLLIElement) => {
                  listRefs.current[index] = el;
                }}>
                <button
                  onClick={() => handleAutoCompleteClick(text)}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      highlightKeyword(text, debouncedValue),
                      {
                        ADD_ATTR: ['target'],
                      },
                    ),
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles.InputField}>
        <SoopInputBox
          className={styles.InputBox}
          clearable={false}
          {...(inputValue ? {iconName: 'icoSend'} : {iconOnly: 'icoSend'})}
          placeholder='궁금한 점을 입력해 주세요.'
          onCustomButtonClick={() => sendMessage(inputValue)}
          value={inputValue}
          onChange={onInputChange}
          onKeyUp={onInputKeyUp}
          onKeyDown={onInputKeyDown}
          onFocus={openDropdown}
          variant='fill'
          iconFill='#0182FF'
          autoComplete='off'
          ref={inputRef}
          maxLength={100}
          showMaxLength={false}
        />
      </div>
    </div>
  );
};

export default ChatbotInputArea;
